/**
 * Projekte samt ihren Aufgaben und dem daraus berechneten Zustand.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';

import { db } from '@/db/schema';
import { PROJECT_STATUSES } from '@/db/repositories/projects';
import { isStuck, projectProgress, type ProjectProgress } from '@/domain/projects/progress';
import { useAreaFilter } from '@/store/areaFilter';
import type { Project, Task } from '@/domain/types';

export interface ProjectWithTasks {
  project: Project;
  tasks: Task[];
  offen: Task[];
  progress: ProjectProgress;
  stuck: boolean;
}

export function useProjects(): ProjectWithTasks[] {
  const { activeAreaIds } = useAreaFilter();

  const rohdaten = useLiveQuery(
    async () => {
      const [projects, tasks] = await Promise.all([
        db.projects.toArray(),
        db.tasks.toArray(),
      ]);
      return { projects, tasks };
    },
    [],
    { projects: [] as Project[], tasks: [] as Task[] },
  );

  return useMemo(() => {
    const nachProjekt = new Map<string, Task[]>();
    for (const t of rohdaten.tasks) {
      if (!t.projectId) continue;
      const liste = nachProjekt.get(t.projectId) ?? [];
      liste.push(t);
      nachProjekt.set(t.projectId, liste);
    }

    return rohdaten.projects
      .filter((p) => activeAreaIds.length === 0
        || (p.areaId !== null && activeAreaIds.includes(p.areaId)))
      .sort(
        (a, b) =>
          PROJECT_STATUSES.indexOf(a.status) - PROJECT_STATUSES.indexOf(b.status)
          || a.title.localeCompare(b.title, 'de'),
      )
      .map((project) => {
        const tasks = (nachProjekt.get(project.id) ?? []).sort((a, b) => a.order - b.order);
        return {
          project,
          tasks,
          offen: tasks.filter((t) => t.status !== 'erledigt'),
          progress: projectProgress(tasks),
          stuck: isStuck(project, tasks),
        };
      });
  }, [rohdaten, activeAreaIds]);
}

/** Projekte fuer Auswahlfelder - nur die, denen man sinnvoll etwas zuordnet. */
export function useAssignableProjects(): Project[] {
  const alle = useLiveQuery(() => db.projects.toArray(), [], [] as Project[]);
  return useMemo(
    () => alle
      .filter((p) => p.status === 'aktiv' || p.status === 'pausiert')
      .sort((a, b) => a.title.localeCompare(b.title, 'de')),
    [alle],
  );
}
